import React, { useState, useRef } from 'react'
import { animated, useSprings } from 'react-spring'
import { useDrag } from 'react-use-gesture'
import styled from '@emotion/styled'

import DotSvg from '../images/dot.svg'

const StyledGallery = styled.div`
  background: var(--color-figure-0025);
  position: relative;

  @media (prefers-color-scheme: dark) {
    background: var(--color-ground-1000);
  }
`

const ImageList = styled.div`
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    display: block;
    padding-top: 150%;
  }

  > ul {
    height: 100%;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  > ul > li {
    list-style: none;
    position: absolute;
    width: 100%;
  }
`

const Button = styled.button`
  display: none;

  @media (hover: hover) {
    appearance: none;
    background: transparent;
    border: none;
    display: block;
    height: 100%;
    margin: 0;
    outline: 0;
    padding: 0;
    position: absolute;
    top: 0;
    width: 50%;
  }
`

const PrevButton = styled(Button)`
  cursor: w-resize;
  left: 0;
`

const NextButton = styled(Button)`
  cursor: e-resize;
  right: 0;
`

const IndicatorList = styled.ul`
  bottom: ${1 / 2}rem;
  display: flex;
  font-size: 0;
  margin: 0;
  padding: 0;
  position: absolute;
  right: ${1 / 2}rem;

  > li {
    list-style: none;
  }
`

const Indicator = styled.button<{ active: boolean }>`
  appearance: none;
  background: none;
  border: none;
  line-height: 0;
  margin: 0;
  outline: 0;
  padding: 2.5px;

  svg {
    height: 5px;
    width: 5px;
  }

  svg circle {
    fill: ${({ active }) =>
      active ? 'var(--color-figure-0500)' : 'var(--color-figure-0250)'};
    transition: fill 0.25s;
  }

  @media (hover: hover) {
    cursor: pointer;
  }
`

interface GalleryProps {
  children: React.ReactNode
}

export const Gallery: React.FC<GalleryProps> = ({ children }) => {
  const [index, setIndex] = useState(0)

  const [springs, setSprings] = useSprings(
    React.Children.toArray(children).length,
    (i) => ({ x: i })
  )

  const { length } = React.Children.toArray(children)
  const widthRef = useRef<HTMLUListElement>(null)
  const indexRef = useRef(0)
  const lengthRef = useRef(length)
  lengthRef.current = length

  const adjust = () => {
    setIndex(indexRef.current)
    setSprings((i) => ({
      x: i - indexRef.current,
      immediate: false,
    }))
  }

  const next = () => {
    indexRef.current = Math.min(indexRef.current + 1, lengthRef.current - 1)
    adjust()
  }

  const prev = () => {
    indexRef.current = Math.max(indexRef.current - 1, 0)
    adjust()
  }

  const bind = useDrag(({ down, movement: [mx], vxvy: [vx] }) => {
    if (!widthRef.current) return

    const width = widthRef.current.offsetWidth

    if (down) {
      setSprings((i) => ({
        x: i - indexRef.current + mx / width,
        immediate: true,
      }))
    } else if (vx < -0.5) {
      next()
    } else if (vx > 0.5) {
      prev()
    } else if (mx < width * -0.5) {
      next()
    } else if (mx > width * 0.5) {
      prev()
    } else {
      adjust()
    }
  })

  const to = (i: number) => () => {
    indexRef.current = Math.min(Math.max(i, 0), lengthRef.current - 1)
    adjust()
  }

  return (
    <StyledGallery>
      <ImageList>
        <ul {...bind()} ref={widthRef}>
          {springs.map(({ x }, i) => (
            <animated.li
              key={i.toString()}
              style={{
                transform: x.to(
                  (value) => `translate3d(${value * 100}%, 0, 0)`
                ),
              }}
            >
              {React.Children.toArray(children)[i]}
            </animated.li>
          ))}
        </ul>
      </ImageList>
      {index > 0 && <PrevButton onClick={prev} />}
      {index < length - 1 && <NextButton onClick={next} />}
      <IndicatorList>
        {React.Children.map(children, (_, i) => (
          <li>
            <Indicator active={i === index} onClick={to(i)}>
              <DotSvg />
            </Indicator>
          </li>
        ))}
      </IndicatorList>
    </StyledGallery>
  )
}
